import { MultilineInput } from '@amelio/ui-library';

import { AutosaveIndicator, type AutosaveState } from './AutosaveIndicator';
import { LevelSelect } from './LevelSelect';
import { ScoreInput } from './ScoreInput';
import { COMMENT_MAX_LENGTH } from '../calibrationConstants';
import { TABLE_CELL } from '../calibrationStyles';

import type { CalibrationPerformanceLevel } from '@/types/calibration';

import { useAmelioTranslations } from '@/hooks';

export interface EditableAdjustmentCellsProps {
    employeeId: string;
    employeeName: string;
    levels: CalibrationPerformanceLevel[];
    score: number | null;
    /** `null` while the row keeps the level the manager gave. */
    levelId: string | null;
    comment: string;
    autosave: AutosaveState;
    onScoreChange: (value: number | null) => void;
    onLevelChange: (levelId: string | null) => void;
    onCommentChange: (value: string) => void;
}

/** Calibrated score, level and comment of a draft (or not-yet-started) adjustment, editable. */
export const EditableAdjustmentCells = ({
    employeeId,
    employeeName,
    levels,
    score,
    levelId,
    comment,
    autosave,
    onScoreChange,
    onLevelChange,
    onCommentChange,
}: EditableAdjustmentCellsProps) => {
    const { t } = useAmelioTranslations();

    return (
        <>
            <td className={TABLE_CELL}>
                <ScoreInput
                    value={score}
                    onChange={onScoreChange}
                    ariaLabel={t('calibration.table.fieldLabel.calibratedScore', {
                        name: employeeName,
                    })}
                    dataTestId={`calibration-score-input-${employeeId}`}
                />
            </td>

            <td className={TABLE_CELL}>
                <LevelSelect
                    levels={levels}
                    value={levelId}
                    onChange={onLevelChange}
                    ariaLabel={t('calibration.table.fieldLabel.calibratedLevel', {
                        name: employeeName,
                    })}
                    dataTestId={`calibration-level-select-${employeeId}`}
                />
            </td>

            <td className={TABLE_CELL}>
                <MultilineInput
                    value={comment}
                    onChange={onCommentChange}
                    placeholder={t('calibration.table.commentPlaceholder')}
                    maxLength={COMMENT_MAX_LENGTH}
                    // Only once there is something to count: fifteen "0 / 2000" counters stacked
                    // down an empty column is noise, not help.
                    showCharacterCount={comment.length > 0}
                    rows={1}
                    ariaLabel={t('calibration.table.fieldLabel.comment', { name: employeeName })}
                    dataTestId={`calibration-comment-input-${employeeId}`}
                />

                <AutosaveIndicator
                    employeeId={employeeId}
                    autosave={autosave}
                />
            </td>
        </>
    );
};
